import { Injectable } from '@angular/core';
import { PlotAttributes, TripAttributes } from '~models';
import { ApiService, Endpoint } from './api.service';
import {
  Observable,
  of,
  interval,
  combineLatest,
  fromEvent,
  BehaviorSubject,
  throwError,
  firstValueFrom,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  share,
  startWith,
  switchMap,
  switchMapTo,
  take,
  tap,
} from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import mime from 'mime';
import { Okay, OkayAttributes } from '~app/models/okay';
import { TextService } from './text.service';
import { PaginationData, PaginationResult } from '~app/models/pagination-data';
import { UserGroupService } from './user-group.service';
import { ATCBActionEventConfig, atcb_action } from 'add-to-calendar-button';
import { LocationStrategy } from '@angular/common';
import { AuthService } from './auth.service';
import dayjs from 'dayjs';
import { LatLng } from 'leaflet';

export const AllowedFileAttachmentContentTypes: string[] = [
  'image/jpeg',
  'image/jpg',
  'image/pjpeg',
  'image/png',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/octet-stream',
] as const;

/** Mutates the original array to be sorted in alphabetical order */
export function alphabetizeAttachments(
  attachmentArray: FileAttachments.AttachedFile[]
) {
  attachmentArray.sort((a, b) => {
    if (a.fileName.toLowerCase() > b.fileName.toLowerCase()) {
      return 1;
    } else if (a.fileName.toLowerCase() < b.fileName.toLowerCase()) {
      return -1;
    }
  });
}
/** attachments - alphabetize and get hrefs */
export function processAttachments(trip: TripAttributes, apiUrl: string) {
  if (!trip.attachments) return;
  trip.attachments.forEach((attachment) => {
    attachment.status = 'uploaded';
    attachment.viewHref = `${apiUrl}/trips/${attachment.tripId}/files/${attachment.id}`;
    attachment.downloadHref = attachment.viewHref + '?download';
    attachment.extension = mime
      .getExtension(attachment.contentType)
      ?.toUpperCase();
  });
  alphabetizeAttachments(trip.attachments);
}
/** process okays and determine which one closed the trip */
export function processOkays(trip: TripAttributes) {
  if (!trip.okays) return;
  trip.okays.sort((a, b) => b.timeOkay.valueOf() - a.timeOkay.valueOf());
  let allCheckIns = trip.okays.filter((okay) => okay.type === 'checkIn');
  let lastCheckIn = allCheckIns[allCheckIns.length - 1];
  if (trip.tripStatus === 'Closed' && lastCheckIn) {
    lastCheckIn.closedTrip = true;
  }
}

export function getLettersForIndex(index: number) {
  const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numberOfDigits = index.toString(26).length;
  let finalLetter = '';
  // Not Using While loop because infinite loops suck.
  for (let x = 0; x < numberOfDigits; x++) {
    if (index < 0) {
      break;
    }
    let remainder = index % 26;
    finalLetter = allLetters[remainder] + finalLetter;
    index = Math.floor(index / 26 - 1);
  }
  return finalLetter;
}

/** process locations and add map point information */
export function processLocations(trip: TripAttributes) {
  trip.locations?.forEach((location, index) => {
    // todo: if location.coords is VALID
    if (location.coords) {
      location.waypoint = {
        locationId: location.id,
        letter: getLettersForIndex(index),
        coords: location.coords,
        latlon: new LatLng(location.coords.lat, location.coords.lon),
        description: location.description,
      };
    }
  });
}

/** process checkpoints, mostly just sorting */
export function processCheckpoints(trip: TripAttributes) {
  trip.checkpoints?.forEach((checkpoint) => {
    checkpoint.complete = ['Submitted', 'Missed'].includes(checkpoint.status);
  });
  trip.checkpoints?.sort((a, b) => {
    if (a.complete && !b.complete) {
      return 1;
    }
    if (b.complete && !a.complete) {
      return -1;
    }
    // a and b are both complete or both incomplete
    return (
      new Date(a.expectedTime).valueOf() - new Date(b.expectedTime).valueOf()
    );
  });
}

/** Make sure equipment has a valid structure */
export function processEquipment(trip: TripAttributes) {
  trip.equipmentList?.forEach((equip) => {
    if (!equip.id) {
      equip.id = uuidv4();
    }
  });
}

export function op_ProcessTripFromAPI(
  userGroupService: UserGroupService,
  apiUrl: string
) {
  return (trip$: Observable<TripAttributes>) => {
    return trip$.pipe(
      // attachments - alphabetize and get hrefs
      tap((trip) => processAttachments(trip, apiUrl)),
      // process okays and determine which one closed the trip
      tap((trip) => processOkays(trip)),
      // Process Locations into Map Points
      tap((trip) => processLocations(trip)),
      tap((trip) => processCheckpoints(trip)),
      tap((trip) => processEquipment(trip)),
      // Set trip.usergroup
      switchMap((trip) =>
        userGroupService.findById(trip.userGroupId).pipe(
          tap((userGroup) => (trip.usergroup = userGroup)),
          mapTo(trip),
          catchError((err, caught) => {
            console.error(err);
            return of(trip);
          })
        )
      )
    );
  };
}

@Injectable({
  providedIn: 'root',
})
export class TripService {
  public readonly MaxIndividualTrips = 10;
  public readonly MinRefreshTime = 1 * 60 * 1000; // 1 minute
  public readonly AllowedFileAttachmentSize = 10 * 1024 * 1024; // 10 MB
  public readonly ErrorCodes = {
    FileType: 'INVALID_FILE_TYPE',
    FileSize: 'INVALID_FILE_SIZE',
  } as const;

  public readonly lastUpdatedDate$ = this.apiService
    .GetSingle<string>('/trips/last-updated')
    .pipe(
      map((date) => new Date(date as string)),
      catchError((err, caught) => {
        return of<Date>();
      })
    );
  public readonly checkForRefreshOnInterval$ = combineLatest([
    interval(10000),
    fromEvent(document, 'visibilitychange').pipe(startWith(0)),
  ]).pipe(
    filter((_) => document.visibilityState === 'visible'),
    startWith(0),
    switchMapTo(this.lastUpdatedDate$),
    distinctUntilChanged((x, y) => x.valueOf() === y.valueOf())
  );
  public readonly endpointName = 'trips';
  public readonly apiUrl = this.apiService.apiUrl;
  private endpoint: Endpoint<TripAttributes>;
  private okayEndpoint: Endpoint<Okay>;

  private trip$ById: {
    [id: string]: ReturnType<TripService['createReusableTripObservable']>;
  } = {};

  constructor(
    private readonly apiService: ApiService,
    private readonly userGroupService: UserGroupService,
    private readonly textService: TextService,
    private readonly locationStrategy: LocationStrategy,
    private readonly authService: AuthService
  ) {
    this.endpoint = apiService.endpoint(this.endpointName);
    this.okayEndpoint = apiService.endpoint(this.endpointName + '/okays');
  }

  /**
   * `addOkay` adds an okay to the list, fetches the trip again, and updates
   * the trip list and the findById result.  `addOkay` returns void,
   * So watch the existing `trip$` for changes.
   */
  addOkay(okay: OkayAttributes): Observable<TripAttributes> {
    let tripId = okay.tripId;
    let okayAdded = this.okayEndpoint.Post(okay);
    return okayAdded.pipe(
      mapTo(this.getReusableTripObservable(tripId)),
      tap((reusable) => reusable.refresh$.next()),
      switchMap((reusable) => reusable.trip$),
      take(1)
    );
  }

  private createReusableTripObservable(tripId: string) {
    const refresh$ = new BehaviorSubject<void>(null);
    const trip$ = refresh$.pipe(
      switchMap(() =>
        this.endpoint
          .Get(tripId)
          .pipe(op_ProcessTripFromAPI(this.userGroupService, this.apiUrl))
      ),
      share<TripAttributes>()
    );
    return {
      status: '' as 'loading' | 'loaded' | 'destroyed' | '',
      lastRefreshed: new Date().valueOf(),
      trip$: trip$,
      refresh$: refresh$,
    };
  }
  private getReusableTripObservable(tripId: string) {
    if (!this.trip$ById[tripId]) {
      this.trip$ById[tripId] = this.createReusableTripObservable(tripId);
    }
    return this.trip$ById[tripId];
  }

  /** A reusuable observable that will refresh whenever the trip is saved by any other tripService.saveTrip call */
  findById(tripId: string): Observable<TripAttributes> {
    let obs = this.getReusableTripObservable(tripId);
    obs.refresh$.next();
    return obs.trip$;
  }
  search(paginationData: Partial<PaginationData>) {
    let fullPaginationData: PaginationData = {
      limit: paginationData.limit ?? 20,
      offset: paginationData.offset ?? 0,
      order: paginationData.order ?? [['updatedAt', 'ASC']],
      where: paginationData.where ?? [],
    };
    return this.apiService.Search<PaginationResult<TripAttributes>>(
      '/trips',
      fullPaginationData
    );
  }

  saveTrip(input: Partial<TripAttributes>): Observable<TripAttributes> {
    let originalRequest = this.endpoint.Put(input.id, input);
    if (!input.id) {
      originalRequest = this.endpoint.Post(input);
    }
    let request = originalRequest.pipe(
      op_ProcessTripFromAPI(this.userGroupService, this.apiUrl),
      tap((trip) => {
        // Refresh Trip regardless of whether the save fails or succeeds.
        this.getReusableTripObservable(trip.id).refresh$.next();
      }),
      catchError((err) => {
        if (input.id) {
          // Refresh Trip regardless of whether the save fails or succeeds.
          this.getReusableTripObservable(input.id).refresh$.next();
        }
        throw err;
      })
    );
    return request;
  }
  deleteTrip(id: uuidv4): Observable<TripAttributes> {
    let request = this.endpoint
      .Delete(id)
      .pipe(tap(() => this.getReusableTripObservable(id).refresh$.next()));
    return request;
  }

  getSelectOption(trip: TripAttributes) {
    let name = trip.crewLeader?.displayName;
    let date = this.textService.formatDateOnly(trip.departureDate);
    return { label: `${name} | ${date}`, value: trip };
  }

  getBlankTripForm(): TripAttributes {
    return {
      id: '',
      tripStatus: 'Planned',
      usergroup: null,
      userGroupId: '',
      startDate: new Date().toISOString(),
      endDate: null,
      locations: [],
      crewMembers: [],
      equipmentList: [],
      checkpoints: [],
      okays: [],
    };
  }

  duplicateTrip({ tripId, startDate, endDate }) {
    return this.apiService.Custom<TripAttributes>(
      'POST',
      `trips/duplicate/${tripId}`,
      {
        startDate,
        endDate,
        duplicate: {
          crewMembers: true,
          locations: true,
          equipmentList: true,
          okays: false,
          attachments: true,
        },
      }
    );
  }

  attachFile(tripId, file: File) {
    // comment out the following two if statements in order to test the backend portion
    if (
      !AllowedFileAttachmentContentTypes.includes(file.type) &&
      !/\.gpx$/.test(file.name) // allow .gpx files to be added
    ) {
      return throwError(() => new Error(this.ErrorCodes.FileType));
    }
    if (file.size > this.AllowedFileAttachmentSize) {
      return throwError(() => new Error(this.ErrorCodes.FileSize));
    }
    const reuse = this.getReusableTripObservable(tripId);
    const formData: FormData = new FormData();
    formData.append('attachment', file, file.name);
    return this.apiService
      .Post<FileAttachments.AttachedFile>(`/trips/${tripId}/attach/`, formData)
      .pipe(tap(() => reuse.refresh$.next()));
  }
  removeAttachment(file: FileAttachments.AttachedFile) {
    const reuse = this.getReusableTripObservable(file.tripId);
    file.status = 'deleting';
    return this.apiService
      .Delete(`/trips/${file.tripId}/files/`, file.id)
      .pipe(tap(() => reuse.refresh$.next()));
  }

  calendarDescriptionAlpha(trip: TripAttributes) {
    const tripUrl =
      location.origin +
      this.locationStrategy.prepareExternalUrl('/trip/id/' + trip.id);
    const parts = [
      `<img src='https://ciao.fs2c.usda.gov/ciao/assets/img/ciao/ciao-logo-brown-full.svg'>`,
      'Here we can have a ciao trip description.  Super customizable.  Sadly, no pictures as far as I can tell.',
      `[url]${tripUrl}|View Trip in CIAO[/url]`,
      `Start Date: ${trip.startDate}`,
      `End Date: ${trip.endDate}`,
      '<h2>Crew</h2>',
      ...trip.crewMembersProcessed.map((person) => '--- ' + person.displayName),
      '<h2>Locations</h2>',
      ...trip.locations.map(
        (location) =>
          '--- ' + location.description + ' (and other location data)'
      ),
    ];
    return parts.join('[br]');
  }

  calendarDescriptionSimple(trip: TripAttributes) {
    const tripUrl =
      location.origin +
      this.locationStrategy.prepareExternalUrl('/trip/id/' + trip.id);
    return [
      `[b]${trip.usergroup?.label ?? 'CIAO'}[/b] Trip`,
      `[h2][url]${tripUrl}|View Trip in CIAO[/url][/h2]`,
    ].join('[br]');
  }

  /**
   *  Make iCal / .isc Calendar Attachment and get it downloaded
   *  Correction: we're going to use `add-to-calendar-button` package because it covers more use cases with less code
   */
  async addToCalendar(trip: TripAttributes) {
    console.log(trip);
    let user = await firstValueFrom(this.authService.currentUser$.pipe(take(1)));
    const config: ATCBActionEventConfig = {
      uid: 'ciao-trip-' + trip.id,
      name: (trip.usergroup?.label ?? 'CIAO') + ' Trip',
      iCalFileName: 'ciao-trip-' + trip.id,
      description: this.calendarDescriptionSimple(trip),
      startDate: trip.startDate,
      endDate: trip.endDate,
      location: trip.locations[0]?.description,
      // timeZone: 'currentBrowser',
      // same as `timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,`
      debug: true,
      // sequence needs to grow when changes are made.  Let's hope there's no upper limit.
      // strike that.  Upper limit is 32-bit integer.  As of 2024, new Date().valueOf() is 41 bits (so I assume 64-bit integer)
      // Divide by milliseconds per minute and round to get at most 28 bits (fits in 32-bit integer) (considering time wayyyy in the future)
      // Must do this because we're not saving the calendar/sequence anywhere.
      sequence: Math.round(new Date().valueOf() / 60000),
      // These must be formatted in YYYYMMDDTHHMMSSZ format, as in: ISO but without any punctuation
      created: dayjs(trip.createdAt).format('YYYYMMDDTHHMMss[Z]'),
      updated: dayjs(trip.updatedAt).format('YYYYMMDDTHHMMss[Z]'),
      organizer: 'CIAO NOTIFICATION|no-reply.sm.fs.ciao_support@usda.gov',
      attendee: user.person.displayName + '|' + user.email,

      options: ['iCal', 'Google', 'Microsoft365', 'MicrosoftTeams'],
      listStyle: 'modal',
      customLabels: {
        ical: 'iCal File (Use this for Outlook)',
      },
    };

    console.log(config);
    atcb_action(config);
  }
}
